import { TFunction } from 'i18next'

import type { WeekScheduleLabels } from '@/components/business/week-schedule'
import { DAY_NAMES_NARROW } from '@/constants/date'
import type { MaintenanceSettings } from '@/models'

import type { MaintenanceSettingsForm } from './maintenance-settings.types'

// Note: days/slot length start empty (unlike Packages' 8 AM-8 PM prefill) so saving an untouched
// schedule surfaces the "nothing selected" errors; hours prefill once a day is switched on.
export const mapSettingsToForm = (settings: MaintenanceSettings): MaintenanceSettingsForm => ({
  businessHours: DAY_NAMES_NARROW.map((day) => {
    const existing = settings.businessHours.find((hours) => hours.name === day)
    return {
      enabled: !!existing,
      endTime: existing?.endTime ?? null,
      name: day,
      startTime: existing?.startTime ?? null,
    }
  }),
  requiredAssignee: settings.requiredAssignee,
  requiredScheduledDate: settings.requiredScheduledDate,
  // Note: BE sends `0` for a slot length that was never chosen.
  slotLength: settings.slotLength || null,
  suggestedTimeframeEnabled: settings.suggestedTimeframeEnabled,
})

export const mapFormToSettings = (
  form: MaintenanceSettingsForm,
  buildingId: number,
): MaintenanceSettings => ({
  buildingId,
  businessHours: form.businessHours.flatMap(({ enabled, name, startTime, endTime }) =>
    enabled && startTime != null && endTime != null ? [{ endTime, name, startTime }] : [],
  ),
  requiredAssignee: form.requiredAssignee,
  requiredScheduledDate: form.requiredScheduledDate,
  slotLength: form.slotLength ?? 0,
  suggestedTimeframeEnabled: form.suggestedTimeframeEnabled,
})

export const getSlotLengthOptions = (t: TFunction<['maintenance']>) => [
  { label: t('maintenance:settings.slotLength.options.oneHour'), value: 1 },
  { label: t('maintenance:settings.slotLength.options.twoHours'), value: 2 },
  { label: t('maintenance:settings.slotLength.options.threeHours'), value: 3 },
  { label: t('maintenance:settings.slotLength.options.fourHours'), value: 4 },
]

export const getWeekScheduleLabels = (t: TFunction<['maintenance']>): WeekScheduleLabels => ({
  days: t('maintenance:settings.schedule.days'),
  endPlaceholder: t('maintenance:settings.schedule.endPlaceholder'),
  hours: t('maintenance:settings.schedule.hours'),
  startPlaceholder: t('maintenance:settings.schedule.startPlaceholder'),
  to: t('maintenance:settings.schedule.to'),
})
